<script lang="ts" setup>
import { useElementHover, useFocus, useWindowScroll, watchDebounced } from '@vueuse/core'

const props = withDefaults(
  defineProps<{
    quantity: number
    minQuantity?: number
    maxQuantity?: number
    size?: 'small' | 'regular' | 'large'
  }>(),
  {
    minQuantity: 1,
    maxQuantity: 100,
    size: 'regular',
  },
)

const emit = defineEmits(['increment', 'decrement', 'update:quantity'])

const isValid = ref({
  valid: true,
  quantity: props.quantity,
})

const inputRef = ref()
const isDirty = ref(false)
const addButtonRef = ref()
const isMobile = useIsMobile()
const { x, y } = useWindowScroll()
const { focused } = useFocus(inputRef)
const isHovered = useElementHover(addButtonRef)
const tooltipPosition = ref<{ top: string; left: string; right: string; width?: string }>({
  top: '0px',
  left: '0px',
  right: '0px',
  width: undefined,
})

watchDebounced(
  focused,
  (isFocused) => {
    isFocused &&
      isMobile.value &&
      inputRef.value?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' })
  },
  { debounce: 100 },
)

const toPx = (val: number) => `${val}px`

const setTooltipPosition = () => {
  const addButtonEl = addButtonRef.value as HTMLElement
  if (!addButtonEl) return {}

  const rect = addButtonEl.getBoundingClientRect()

  if (isMobile.value) {
    tooltipPosition.value = {
      top: toPx(-55),
      right: toPx(16),
      left: 'inherit',
    }

    return
  }

  tooltipPosition.value = {
    top: toPx(-55),
    left: toPx(rect.width / 2 - 86),
    right: 'inherit',
  }
}

watch([x, y], () => {
  setTooltipPosition()
})

const inputSizeClassMap = {
  small: 'py-0 px-4',
  regular: 'py-2 px-4',
  large: 'py-4 px-6',
}

const iconSizeMap = {
  small: '26',
  regular: '20',
  large: '32',
}

const increment = () => {
  if (props.quantity + 1 > props.maxQuantity) return

  emit('increment')
}

const decrement = () => {
  if (props.quantity - 1 < props.minQuantity) return

  emit('decrement')
}

const handleInput = (e: Event) => {
  const el = e.target as HTMLInputElement
  el.value = el.value.replace(/\D/gim, '')

  const quantity = Number(el.value)

  setValidStatus(quantity)

  emit('update:quantity', quantity)
}

const setValidStatus = (quantity: number) => {
  isValid.value = {
    quantity,
    valid: quantity >= 0 && quantity <= props.maxQuantity,
  }
}

watch([() => props.maxQuantity, () => props.minQuantity], () => {
  setValidStatus(props.quantity)
})

onMounted(() => {
  setTooltipPosition()
  setValidStatus(props.quantity)
})
</script>

<template>
  <div class="quantity-input relative" v-bind="$attrs">
    <RTransition name="grow-fade">
      <div
        v-if="(isHovered || focused) && !isValid.valid"
        :style="tooltipPosition"
        class="tooltip absolute mx-4 w-[300px] rounded-md bg-gray-400 px-4 py-2 text-white shadow-md sm:mx-0"
      >
        <div class="arrow"></div>
        {{
          !isValid.quantity
            ? $t('components.cartQuantityInput.minimumStockTooltip')
            : $t('components.cartQuantityInput.maximumStockTooltip', {
                quantityAvailable: props.maxQuantity,
              })
        }}
      </div>
    </RTransition>

    <div
      class="flex h-12 w-full justify-between rounded-full border-1 border-gray-100 bg-white text-gray-300"
      :class="[
        inputSizeClassMap[props.size],
        { 'border-[1.5px] border-red-300': !isValid.valid && isDirty },
      ]"
    >
      <button class="flex-1 py-0" @click="decrement">
        <RIcon :size="iconSizeMap[props.size]" name="ic:round-minus" />
      </button>

      <input
        ref="inputRef"
        maxlength="3"
        class="flex w-[60px] flex-1 items-center justify-center text-center text-xl font-medium text-gray-400"
        :value="quantity"
        @input="handleInput"
        @focus="isDirty = true"
      />

      <button
        ref="addButtonRef"
        :disabled="props.quantity + 1 > props.maxQuantity"
        class="flex-1 py-0 disabled:cursor-not-allowed disabled:text-gray-200"
        @click="increment"
      >
        <RIcon :size="iconSizeMap[props.size]" name="ic:round-plus" />
      </button>
    </div>
  </div>
</template>

<style scoped>
.arrow {
  position: absolute;
  bottom: -14px;
  right: 50%;
  transform: translateX(50%);
  height: 0;
  width: 0;
  border-style: solid;
  border-width: 16px 12px 0 12px;
  border-color: #535353 transparent transparent transparent;

  @media screen and (max-width: 500px) {
    right: 48px;
  }
}
</style>
